import * as React from 'react';
import {
    Form,
    FormGroup,
    ControlLabel,
    FormControl,
    Modal,
    Button,
    Row,
    Col
} from 'react-bootstrap';
import { Translate } from '../../shared/LanguageService';
import DateTime from 'react-datetime';
import FormSelect from '../../components/common/FormSelect';
import WysiwygEditor from '../../components/common/editor/Editor';
import * as Actions from './actions/CalendarActions';
import notify from '../../shared/feedback/notifications';
import { connect } from 'react-redux';
import Loader from 'react-loader';
import moment from 'moment';

interface IActions {
    createEmail: (
        fromEmail: string,
        toEmail: string,
        subject: string,
        body: string,
        cc?: string,
        desiredSendTime?: Date
    ) => Promise<any>;
    getTemplates: (photographerId: number) => Promise<any>;
    getSenders: () => Promise<any>;
}

interface IRecipient {
    photographerId: number;
    name: string;
    email: string;
}

interface ISenderEmailAddress {
    address: string;
}

interface ISender {
    id: number;
    name: string;
    displayNameTranslationKey: string;
    defaultForOrder: boolean;
    emailAddresses: ISenderEmailAddress[];
}

interface IProps {
    actions: IActions;
    onClose: () => void;
    recipient: IRecipient;
    templates: any[];
    senders: ISender[];
}

interface IState {
    desiredSendTime: Date;
    cc: string;
    subject: string;
    body: string;
    datafetched: boolean;
    sender: any;
    editorKey: number;
    templateName: string;
}

class EmailModal extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            desiredSendTime: null,
            subject: '',
            body: '',
            cc: null,
            sender: {},
            datafetched: false,
            editorKey: 0,
            templateName: ''
        };

        this.onBodyChange = this.onBodyChange.bind(this);
        this.sendEmail = this.sendEmail.bind(this);
    }

    componentDidMount() {
        return this.props.actions
            .getTemplates(this.props.recipient.photographerId)
            .then(this.props.actions.getSenders)
            .then(() => {
                this.setState({ datafetched: true } as IState);
                let defaultTemplate = this.props.templates.find(
                    (x) => x.key === 15 /* Default template for emails */
                );
                if (defaultTemplate) {
                    this.onTemplateChanged({
                        target: { value: defaultTemplate.name }
                    });
                }
                let defaultSender = this.findDefaultSender();
                if (defaultSender) {
                    this.setState({
                        sender: defaultSender.emailAddresses[0].address
                    } as IState);
                }
            });
    }

    findDefaultSender = (): ISender => {
        let senders = this.props.senders || [];
        let hasAddress = (s: ISender) => Boolean(s.emailAddresses && s.emailAddresses.length > 0);

        return (
            senders.find((s) => s.defaultForOrder && hasAddress(s)) ||
            senders.find(hasAddress)
        );
    };

    onDateTimeChange = (moment: any) => {
        this.setState({
            desiredSendTime: moment ? moment.toDate() : null
        } as IState);
    };

    onBodyChange(body: string): void {
        this.setState({ body } as IState);
    }

    onTemplateChanged = (e: any) => {
        this.setState({
            body: this.props.templates.find((x) => x.name === e.target.value).content,
            editorKey: this.state.editorKey + 1,
            templateName: e.target.value
        } as IState);
    };

    sendEmail = () => {
        this.props.actions
            .createEmail(
                this.state.sender,
                this.props.recipient.email,
                this.state.subject,
                this.state.body,
                this.state.cc,
                this.state.desiredSendTime
            )
            .then(() => {
                notify.success(Translate('photographer-mail-modal/success'));
                this.props.onClose();
            })
            .catch(() => {
                notify.failure(Translate('photographer-mail-modal/error'));
            });
    };

    render() {
        return (
            <div>
                {!this.state.datafetched && (
                    <div style={{ height: '50vh' }}>
                        <Loader loaded={this.state.datafetched}></Loader>
                    </div>
                )}
                <Modal show={this.state.datafetched} onHide={this.props.onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {Translate('photographer-mail-modal/title', {
                                name: this.props.recipient.name,
                                email: this.props.recipient.email
                            })}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('photographer-mail-modal/sender')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="sender"
                                            value={this.state.sender}
                                            onChange={(e: any) =>
                                                this.setState({
                                                    sender: e.target.value
                                                } as IState)
                                            }
                                        >
                                            {this.props.senders &&
                                                this.props.senders
                                                    .filter(
                                                        (s) =>
                                                            s.emailAddresses &&
                                                            s.emailAddresses.length > 0
                                                    )
                                                    .map((s) => (
                                                        <option
                                                            key={s.id}
                                                            value={s.emailAddresses[0].address}
                                                        >
                                                            {Translate(
                                                                s.displayNameTranslationKey
                                                            ) + ` <${s.emailAddresses[0].address}>`}
                                                        </option>
                                                    ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('photographer-mail-modal/cc')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="cc"
                                            value={this.state.cc}
                                            onChange={(e: any) =>
                                                this.setState({
                                                    cc: e.target.value
                                                } as IState)
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('photographer-mail-modal/when')}
                                        </ControlLabel>
                                        <DateTime
                                            inputProps={{
                                                name: 'sendout-time',
                                                autoComplete: 'off'
                                            }}
                                            locale={moment.locale()}
                                            onChange={this.onDateTimeChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('photographer-mail-modal/template')}
                                        </ControlLabel>
                                        <FormSelect
                                            name={'template'}
                                            value={this.state.templateName}
                                            onChange={this.onTemplateChanged}
                                        >
                                            {this.props.templates &&
                                                this.props.templates.map((t, i) => (
                                                    <option key={`${t.name}-${i}`} value={t.name}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup>
                                <ControlLabel>
                                    {Translate('photographer-mail-modal/subject')}
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    name="subject"
                                    required
                                    value={this.state.subject}
                                    onChange={(e: any) =>
                                        this.setState({
                                            subject: e.target.value
                                        } as IState)
                                    }
                                />
                            </FormGroup>

                            <WysiwygEditor
                                key={`mail-editor-${this.state.editorKey}`}
                                onChange={this.onBodyChange}
                                content={this.state.body}
                                height={300}
                                addImageModal={false}
                                onImageUploaded={() => {}}
                            />
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.props.onClose}>
                            {Translate('photographer-mail-modal/close')}
                        </Button>
                        <Button onClick={this.sendEmail} bsStyle="primary">
                            {Translate('photographer-mail-modal/send')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        templates: state.photographerCalendars.emailTemplatesForPhotographer,
        senders: state.photographerCalendars.emailSendersForPhotographer
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            createEmail: (
                fromEmail: string,
                toEmail: string,
                subject: string,
                body: string,
                cc?: string,
                desiredSendTime?: Date
            ) =>
                dispatch(
                    Actions.createEmail(fromEmail, toEmail, subject, body, cc, desiredSendTime)
                ),
            getTemplates: (photographerId: number) =>
                dispatch(Actions.getEmailTemplates(photographerId)),
            getSenders: () => dispatch(Actions.getEmailSenders())
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailModal);
